if (!customElements.get('meu-home')) {
  // Colours and the flowing background come from meutant-gradient.js
  const { clamp, reducedMotion, parseHex, palette, analyse, productPalette, Backdrop } = window.MeuGradient;

  /* ---------- Mobile: vertical looping carousel ---------- */

  class Carousel {
    constructor(root, onSelect) {
      this.root = root;
      this.onSelect = onSelect;
      this.slides = Array.from(root.querySelectorAll('.meu-slide'));
      this.inners = this.slides.map((slide) => slide.querySelector('.meu-slide__inner'));
      this.links = this.slides.map((slide) => Array.from(slide.querySelectorAll('a, button')));
      this.images = this.slides.map((slide) => Array.from(slide.querySelectorAll('img')));
      this.count = this.slides.length;
      this.pos = 0;
      this.target = 0;
      this.selected = -1;
      this.wheelTotal = 0;
      this.step = this.step.bind(this);

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = () => {
        this.measure();
        this.render();
      };

      root.addEventListener('pointerdown', this.onPointerDown);
      root.addEventListener('click', this.onClick, true);
      root.addEventListener('wheel', this.onWheel, { passive: false });
      root.addEventListener('keydown', this.onKeydown);
      root.addEventListener('dragstart', (event) => event.preventDefault());
      root.addEventListener('focusin', (event) => {
        const index = this.slides.indexOf(event.target.closest('.meu-slide'));
        if (index >= 0 && index !== this.selected) this.goTo(this.target + this.offset(index, this.target));
      });
      window.addEventListener('resize', this.onResize);

      this.measure();
      this.goTo(0, true);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    // Shortest signed distance (in slides) from position `from` to slide `index`, wrapping around
    offset(index, from = this.pos) {
      let d = (((index - from) % this.count) + this.count) % this.count;
      if (d > this.count / 2) d -= this.count;
      return d;
    }

    measure() {
      this.height = this.root.clientHeight;
      this.slideHeight = this.height * 0.55;
      this.top = (this.height - this.slideHeight) / 2;
    }

    render() {
      if (!this.height) return;
      for (let i = 0; i < this.count; i++) {
        const d = this.offset(i);
        const distance = Math.abs(d);
        const slide = this.slides[i];
        if (distance > 2.6) {
          slide.style.visibility = 'hidden';
          continue;
        }
        slide.style.visibility = '';
        slide.style.transform = `translate3d(0, ${(this.top + d * this.slideHeight).toFixed(2)}px, 0)`;
        const scale = 1 - 0.5 * Math.min(distance, 1);
        const blur = 10 * (1 - Math.pow(1 - Math.min(distance / 2, 1), 2));
        this.inners[i].style.transform = `scale(${scale.toFixed(4)})`;
        this.inners[i].style.filter = blur > 0.05 ? `blur(${blur.toFixed(2)}px)` : 'none';
        this.images[i].forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';
        });
      }
    }

    goTo(target, instant = false) {
      this.target = target;
      const index = ((Math.round(target) % this.count) + this.count) % this.count;
      if (index !== this.selected) {
        this.selected = index;
        this.links.forEach((links, i) => links.forEach((link) => link.setAttribute('tabindex', i === index ? '0' : '-1')));
        this.onSelect(index);
      }
      if (instant || reducedMotion()) {
        this.pos = target;
        this.render();
        return;
      }
      if (!this.animating) {
        this.animating = true;
        this.last = performance.now();
        requestAnimationFrame(this.step);
      }
    }

    step(now) {
      if (this.dragging) {
        this.animating = false;
        return;
      }
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      const diff = this.target - this.pos;
      if (Math.abs(diff) < 0.0005) {
        this.pos = this.target;
        this.render();
        this.animating = false;
        // Keep the numbers small after many swipes
        const wraps = Math.floor(this.pos / this.count) * this.count;
        this.pos -= wraps;
        this.target -= wraps;
        return;
      }
      this.pos += diff * (1 - Math.exp(-dt * 11));
      this.render();
      requestAnimationFrame(this.step);
    }

    onPointerDown(event) {
      if (event.button !== 0 || this.count < 2) return;
      this.dragging = true;
      this.moved = false;
      this.pointerId = event.pointerId;
      this.startY = event.clientY;
      this.startPos = this.pos;
      this.lastY = event.clientY;
      this.lastTime = performance.now();
      this.velocity = 0;
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      const dy = event.clientY - this.startY;
      if (Math.abs(dy) > 6) this.moved = true;
      const now = performance.now();
      const dt = Math.max(1, now - this.lastTime) / 1000;
      const speed = -(event.clientY - this.lastY) / this.slideHeight / dt;
      this.velocity = this.velocity * 0.6 + speed * 0.4;
      this.lastY = event.clientY;
      this.lastTime = now;
      this.pos = this.startPos - dy / this.slideHeight;
      this.render();
    }

    onPointerUp(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      if (performance.now() - this.lastTime > 120) this.velocity = 0;
      // A quick flick moves one product even if the finger barely travelled
      const projected = this.pos + clamp(this.velocity * 0.25, -0.65, 0.65);
      this.goTo(Math.round(projected));
    }

    onClick(event) {
      if (this.moved) {
        event.preventDefault();
        event.stopPropagation();
        this.moved = false;
        return;
      }
      const index = this.slides.indexOf(event.target.closest('.meu-slide'));
      if (index < 0) return;
      const d = this.offset(index);
      // Tapping a product above or below brings it to the middle instead of opening
      // it (or adding it to the cart)
      if (Math.abs(d) > 0.5) {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(Math.round(this.pos + d));
      }
    }

    onWheel(event) {
      event.preventDefault();
      if (this.wheelLocked) return;
      this.wheelTotal += event.deltaY;
      if (Math.abs(this.wheelTotal) < 30) return;
      this.goTo(Math.round(this.target) + Math.sign(this.wheelTotal));
      this.wheelTotal = 0;
      this.wheelLocked = true;
      setTimeout(() => (this.wheelLocked = false), 450);
    }

    onKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(Math.round(this.target) + 1);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(Math.round(this.target) - 1);
      }
    }
  }

  /* ---------- The homepage element ---------- */

  class MeuHome extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia('(min-width: 1000px)');
      const fallback = palette(parseHex(this.dataset.default) || [143, 213, 255], { keepLightness: true });
      this.defaultScene = { left: fallback, right: fallback };
      this.restingScene = this.defaultScene;
      this.backdrop = new Backdrop(this.querySelector('.meu-home__bg'), this);
      this.backdrop.set(this.defaultScene, true);

      this.setupGrid();
      this.trimPhotos();

      const carouselRoot = this.querySelector('.meu-home__carousel');
      if (carouselRoot && carouselRoot.querySelector('.meu-slide')) {
        this.carousel = new Carousel(carouselRoot, (index) => this.showSlide(index));
      }

      // On desktop the background rests on the drop's colours (the pair at the top),
      // or on a category page, on its first product's colours
      const resting =
        this.querySelector('.meu-tile--pair') ||
        (this.dataset.resting === 'first' ? this.querySelector('.meu-tile') : null);
      if (resting) {
        this.sceneFor(resting).then((scene) => {
          this.restingScene = scene;
          if (this.desktop.matches && !this.hovered) this.backdrop.set(scene);
        });
      }

      this.setupHint();

      // Arrow: scrolls down on desktop, moves to the next product on mobile
      this.more = this.querySelector('[data-more]');
      if (this.more) {
        this.more.addEventListener('click', () => {
          if (this.desktop.matches) {
            window.scrollBy({ top: window.innerHeight * 0.8, behavior: reducedMotion() ? 'auto' : 'smooth' });
          } else if (this.carousel) {
            this.carousel.goTo(Math.round(this.carousel.target) + 1);
          }
        });
        this.onScroll = () => {
          if (this.scrollQueued) return;
          this.scrollQueued = true;
          requestAnimationFrame(() => {
            this.scrollQueued = false;
            this.updateMore();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      this.onModeChange = () => this.applyMode();
      this.desktop.addEventListener('change', this.onModeChange);
      this.applyMode(true);

      requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    disconnectedCallback() {
      this.backdrop?.destroy();
      this.carousel?.destroy();
      this.revealer?.disconnect();
      this.trimmer?.disconnect();
      this.desktop?.removeEventListener('change', this.onModeChange);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.removeHintListeners();
    }

    // The arrow goes once there's nothing more to see: at the bottom on desktop,
    // or when there's only one product
    updateMore() {
      if (!this.more) return;
      const onlyOne = this.querySelectorAll('.meu-tile').length < 2;
      const atBottom =
        this.desktop.matches &&
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 80;
      this.more.classList.toggle('is-hidden', onlyOne || atBottom);
    }

    // TikTok-style "Swipe up for more" hint: shown once per visit, and gone as soon as
    // the visitor taps, clicks, scrolls, swipes or presses a key
    setupHint() {
      const hint = this.querySelector('[data-hint]');
      if (!hint) return;
      let seen = false;
      try {
        seen = sessionStorage.getItem('meu-hint-seen') === '1';
      } catch (error) {
        // Private browsing etc.: just show it
      }
      if (seen || this.querySelectorAll('.meu-tile').length < 2) {
        hint.remove();
        return;
      }
      this.hint = hint;
      const dismiss = () => this.dismissHint();
      this.hintListeners = [
        [hint, 'pointerdown', (event) => {
          event.preventDefault();
          dismiss();
          // The same swipe carries on and moves the products, so the hint is "live"
          if (!this.desktop.matches && this.carousel) this.carousel.onPointerDown(event);
        }],
        [window, 'wheel', dismiss, { passive: true }],
        [window, 'scroll', dismiss, { passive: true }],
        [window, 'touchmove', dismiss, { passive: true }],
        [window, 'keydown', dismiss],
      ];
      this.hintListeners.forEach(([target, type, handler, options]) => target.addEventListener(type, handler, options));
      this.hintTimer = setTimeout(() => {
        hint.hidden = false;
        requestAnimationFrame(() => hint.classList.add('is-visible'));
      }, 600);
    }

    dismissHint() {
      const hint = this.hint;
      if (!hint) return;
      this.hint = null;
      clearTimeout(this.hintTimer);
      this.removeHintListeners();
      try {
        sessionStorage.setItem('meu-hint-seen', '1');
      } catch (error) {
        // Not remembered; it simply shows again next time
      }
      hint.classList.remove('is-visible');
      setTimeout(() => hint.remove(), 450);
    }

    removeHintListeners() {
      this.hintListeners?.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
      this.hintListeners = null;
    }

    colourFor(link) {
      if (!link.meuPalette) {
        link.meuPalette = productPalette(link.dataset.hex, link.dataset.img).then(
          (value) => value || this.defaultScene.left
        );
      }
      return link.meuPalette;
    }

    // Pull each photo's see-through empty space in, so the product sits right above its
    // "[ Add to cart ]" and is centred properly. Re-measured whenever a photo resizes.
    trimPhotos() {
      if (!('ResizeObserver' in window)) return;
      this.trimmer = new ResizeObserver((entries) => {
        entries.forEach(({ target: img }) => {
          const height = img.offsetHeight;
          img.style.marginTop = `${(-height * Number(img.dataset.trimTop || 0)).toFixed(1)}px`;
          img.style.marginBottom = `${(-height * Number(img.dataset.trimBottom || 0)).toFixed(1)}px`;
        });
      });
      this.querySelectorAll('a[data-img]').forEach((link) => {
        const img = link.querySelector('img');
        if (!img) return;
        analyse(link.dataset.img).then((result) => {
          if (!result || (!result.top && !result.bottom)) return;
          // Leave a little breathing room so nothing gets clipped
          img.dataset.trimTop = Math.max(0, result.top - 0.02);
          img.dataset.trimBottom = Math.max(0, result.bottom - 0.02);
          this.trimmer.observe(img);
        });
      });
    }

    // Background for a tile or slide: one product fills both sides, a pair splits left/right
    sceneFor(container) {
      const links = container.querySelectorAll('[data-img]');
      const first = links[0];
      const last = links[links.length - 1];
      if (!first) return Promise.resolve(this.defaultScene);
      return Promise.all([this.colourFor(first), this.colourFor(last)]).then(([left, right]) => ({ left, right }));
    }

    setupGrid() {
      const tiles = Array.from(this.querySelectorAll('.meu-tile'));
      tiles.forEach((tile) => {
        const enter = () => {
          this.hovered = tile;
          this.sceneFor(tile).then((scene) => {
            if (this.hovered === tile && this.desktop.matches) this.backdrop.set(scene);
          });
        };
        const leave = () => {
          if (this.hovered !== tile) return;
          this.hovered = null;
          if (this.desktop.matches) this.backdrop.set(this.restingScene);
        };
        // The whole spot counts, including "[ Add to cart ]" under the photo
        tile.addEventListener('mouseenter', enter);
        tile.addEventListener('mouseleave', leave);
        tile.addEventListener('focusin', enter);
        tile.addEventListener('focusout', (event) => {
          if (!tile.contains(event.relatedTarget)) leave();
        });
        // Work the colours out early so hovering is instant
        this.sceneFor(tile);
      });

      // More products fade in as you scroll down
      if (!('IntersectionObserver' in window) || reducedMotion()) return;
      this.classList.add('is-revealing');
      this.revealer = new IntersectionObserver(
        (entries) => {
          let order = 0;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.style.setProperty('--meu-delay', `${order * 90}ms`);
            entry.target.classList.add('is-in');
            this.revealer.unobserve(entry.target);
            order += 1;
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.1 }
      );
      tiles.forEach((tile) => this.revealer.observe(tile));
    }

    showSlide(index) {
      const slide = this.carousel?.slides[index];
      if (!slide) return;
      // Any open size choice closes when you move on to another product
      this.querySelectorAll('meu-quick-add.is-choosing').forEach((quickAdd) => quickAdd.close?.());
      this.sceneFor(slide).then((scene) => {
        if (this.carousel.selected === index && !this.desktop.matches) this.backdrop.set(scene);
      });
    }

    applyMode(first = false) {
      if (this.desktop.matches) {
        this.backdrop.set(this.restingScene, first);
      } else if (this.carousel) {
        this.carousel.measure();
        this.carousel.render();
        this.showSlide(this.carousel.selected);
      }
      this.updateMore();
    }
  }

  customElements.define('meu-home', MeuHome);
}
